import { useEffect, useState } from 'react';

type UseHasVideoBeenPlayedParams = {
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  mounted?: boolean;
  autoPlay?: boolean;
};

/**
 * Custom hook to track whether a video has been played at least once.
 * Listens for the 'play' event on the video element and also checks
 * if the video is already playing (useful for autoplay scenarios).
 * If autoPlay is true, immediately returns true without setting up listeners.
 */
export const useHasVideoBeenPlayed = ({
  videoRef,
  mounted,
  autoPlay = false
}: UseHasVideoBeenPlayedParams) => {
  const [hasBeenPlayed, setHasBeenPlayed] = useState(autoPlay);

  useEffect(() => {
    // If autoplay is enabled, consider the video as already played
    if (autoPlay) {
      return undefined;
    }

    const currentVideoRef = videoRef?.current;

    const handlePlay = () => {
      setHasBeenPlayed(true);
    };

    if (currentVideoRef) {
      currentVideoRef.addEventListener('play', handlePlay);

      // Also check if video is already playing (in case autoPlay started it)
      if (!currentVideoRef.paused) {
        setHasBeenPlayed(true);
      }
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.removeEventListener('play', handlePlay);
      }
    };
  }, [videoRef, mounted, autoPlay]);

  return hasBeenPlayed;
};
